import { template as template_542080cb15f94fae88299a4911e57428 } from "@ember/template-compiler";
import { service } from '@ember/service';
import { all, task, timeout } from 'ember-concurrency';
import getDelayTime from 'eflex/util/get-delay-time';
import { AnimationTypes } from 'eflex/constants/jem/instruction-animation-types';
import { TextTypes, EflexObjTypes } from 'eflex/constants/work-instructions/tool-props';
import * as Fabric from 'fabric';
import { clone } from 'ramda';
import { dequal } from 'dequal';
import { isBlank, isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import { getCanvasObjectsByType, getCanvasObjectByPoint, getCanvasObjectById, getCanvasCoordsForClick, updateObjectProperty, getArrowsByLine, scaleCanvas as origScaleCanvas, cleanupCanvas, initializeCanvas } from 'eflex/util/fabric-helpers';
import { waitFor } from '@ember/test-waiters';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { onResize } from 'eflex/modifiers';
import Spinner from 'eflex/components/spinner';
import { on } from '@ember/modifier';
import { DataTypes, ModelValues, BOMValues } from 'eflex/constants/jem/instruction-data-types';
import { generatesSerialNumber } from 'eflex/constants/station-options';
const ANIMATION_DURATION = 200;
const renderAllSafe = (canvas)=>{
    try {
        canvas.renderAll();
    } catch (e) {
        // Don't care if animation is interrupted midway.
        if (!(e instanceof TypeError)) {
            throw e;
        }
    }
};
const setLineColor = (lineGroup, strokeColor)=>{
    if (strokeColor == null) {
        return;
    }
    lineGroup.getObjects()?.forEach(function(obj) {
        switch(obj.eflex?.type){
            case EflexObjTypes.STRAIGHT_LINE:
                {
                    obj.set('stroke', strokeColor);
                    break;
                }
            case EflexObjTypes.ARROWHEAD:
                {
                    obj.set({
                        fill: strokeColor,
                        stroke: strokeColor
                    });
                    break;
                }
        }
    });
};
const setFill = (obj, fillColor)=>{
    if (obj.fill) {
        obj.set('fill', fillColor);
    }
    if (obj.stroke) {
        obj.set('stroke', fillColor);
    }
};
const setIconColor = (icon, fillColor)=>{
    if (fillColor == null) {
        return;
    }
    if (icon.type === 'group') {
        icon.getObjects().forEach((obj)=>{
            setFill(obj, fillColor);
        });
    } else {
        setFill(icon, fillColor);
    }
};
const getComplexOptionValue = (triggerConfig, buildData)=>{
    const componentId = triggerConfig?.component?.id;
    const components = buildData?.bomSource.components.filter((item)=>!item.isDeleted);
    const componentData = buildData?.components?.filter((item)=>!item.isAlwaysRun).filter((component)=>components.some((item)=>item.id === component.id));
    const matchedComponent = componentData.find((component)=>component.id === componentId);
    return matchedComponent?.rawValue;
};
const getOptionValue = (triggerConfig, textValue, buildData)=>{
    const option = triggerConfig?.configOption;
    let optionValue;
    if (!isBlank(buildData)) {
        optionValue = getComplexOptionValue(triggerConfig, buildData);
    } else {
        optionValue = textValue;
    }
    return option?.isComplex ? optionValue : option?.value;
};
const updateTextValue = (animation, textValue, buildData, processData)=>{
    const triggerConfig = animation?.workInstructionHardwareTriggerConfig?.taskConfig;
    if (!isBlank(processData) && !isBlank(animation?.processDataOptionId)) {
        return processData;
    } else if (isBlank(buildData?.variables) || isBlank(animation?.selectedValueOptionId)) {
        return updateModelOrBomTextValue(animation, triggerConfig, textValue, buildData);
    } else {
        return updateVariableTextValue(animation, textValue, buildData);
    }
};
const updateVariableTextValue = (animation, textValue, buildData)=>{
    const matchedVariable = buildData?.variables.find((variable)=>variable.id === animation.selectedValueOptionId);
    return matchedVariable?.rawValue ?? textValue;
};
const updateModelOrBomTextValue = (animation, triggerConfig, textValue, buildData)=>{
    const selected = animation?.selectedValueOption;
    if (isBlank(triggerConfig) || isBlank(selected)) {
        return textValue;
    }
    let newValue = null;
    if (triggerConfig.usesComponents) {
        const option = triggerConfig?.configOption;
        switch(selected){
            case BOMValues.NAME:
                {
                    newValue = option?.name;
                    break;
                }
            case BOMValues.VALUE:
                {
                    newValue = getOptionValue(triggerConfig, textValue, buildData);
                    break;
                }
            default:
                {
                    newValue = selected;
                }
        }
    } else if (triggerConfig.usesModels) {
        const model = triggerConfig.configModel;
        newValue = selected === ModelValues.NAME ? model?.name : model?.code;
    }
    if (textValue !== newValue) {
        textValue = newValue;
    }
    return textValue;
};
const setTextboxColor = (obj, animation, buildData, processData)=>{
    const dataType = animation?.dataType ?? null;
    const style = obj.eflex?.originalStyles[0]?.style;
    let textValue = animation?.textValue ?? obj.eflex?.originalTextValue;
    let length = obj?.text?.length;
    const color = animation?.color ?? style?.fill;
    const textBoxColor = animation?.textBoxColor ?? obj.eflex?.originalTextBackgroundColor;
    const strokeColor = animation?.strokeColor ?? style?.stroke;
    const fillColor = animation?.fillColor ?? style?.textBackgroundColor;
    const fontSize = style?.fontSize;
    const fontFamily = style?.fontFamily;
    const fontWeight = style?.fontWeight;
    const linethrough = style?.linethrough;
    const underline = style?.underline;
    const strokeWidth = style?.strokeWidth;
    if (!isBlank(textValue)) {
        if (dataType !== DataTypes.TEXT) {
            textValue = updateTextValue(animation, textValue, buildData, processData);
        }
        obj.set('text', String(textValue));
        length = textValue?.length || 0;
    }
    if (color != null) {
        obj.setSelectionStyles({
            fill: color
        }, 0, length);
    }
    if (strokeColor != null) {
        obj.setSelectionStyles({
            stroke: strokeColor
        }, 0, length);
        obj.dirty = true;
    }
    if (textBoxColor != null) {
        obj.set('backgroundColor', textBoxColor);
    }
    if (fillColor != null) {
        obj.setSelectionStyles({
            textBackgroundColor: fillColor
        }, 0, length);
    }
    if (fontSize != null) {
        obj.setSelectionStyles({
            fontSize
        }, 0, length);
    }
    if (fontFamily != null) {
        obj.setSelectionStyles({
            fontFamily
        }, 0, length);
    }
    if (fontWeight != null) {
        obj.setSelectionStyles({
            fontWeight
        }, 0, length);
    }
    if (linethrough != null) {
        obj.setSelectionStyles({
            linethrough
        }, 0, length);
    }
    if (underline != null) {
        obj.setSelectionStyles({
            underline
        }, 0, length);
    }
    if (strokeWidth != null) {
        obj.setSelectionStyles({
            strokeWidth
        }, 0, length);
    }
};
const setColor = (obj, animation, buildData, processData, allowNull = false)=>{
    const type = obj.eflex?.type ?? obj.type;
    switch(type){
        case EflexObjTypes.LINE_GROUP:
            {
                setLineColor(obj, animation.strokeColor);
                break;
            }
        case EflexObjTypes.ICON:
            {
                setIconColor(obj, animation.fillColor);
                break;
            }
        case TextTypes.TEXTBOX:
            {
                setTextboxColor(obj, animation, buildData, processData);
                break;
            }
        default:
            {
                if (animation.fillColor != null || allowNull) {
                    obj.set('fill', animation.fillColor);
                }
                if (animation.strokeColor != null || allowNull) {
                    obj.set('stroke', animation.strokeColor);
                }
            }
    }
};
const notifyModified = (canvas, target)=>{
    if (!target) {
        return;
    }
    canvas.fire('object:modified', {
        target
    });
    canvas.renderAll();
};
const prepareLineObjects = (canvas, previewMode)=>{
    const lines = getCanvasObjectsByType(canvas, EflexObjTypes.STRAIGHT_LINE);
    lines.forEach((line)=>{
        const arrowheads = getArrowsByLine(canvas, line);
        const layer = canvas.getObjects().indexOf(line);
        const id = line.id;
        line.id = undefined;
        const objects = [
            line,
            ...arrowheads
        ];
        objects.forEach((obj)=>{
            obj.opacity = 1;
        });
        const group = new Fabric.Group(objects);
        group.set({
            id,
            selectable: false,
            editable: false,
            eflex: {
                type: EflexObjTypes.LINE_GROUP,
                startHidden: line.eflex.startHidden,
                originalStrokeColor: line.eflex.originalStrokeColor
            }
        });
        canvas.remove(...objects);
        canvas.add(group);
        canvas.moveObjectTo(group, layer);
        if (line.eflex.startHidden && !previewMode) {
            group.opacity = 0;
        }
    });
};
const scaleCanvas = (element, canvas, workInstruction)=>{
    const display = element.querySelector('.work-instruction-display');
    if (display == null) {
        return;
    }
    const { width, height } = display.getBoundingClientRect();
    origScaleCanvas(canvas, width, height, workInstruction?.width, workInstruction?.height);
};
const setStartHiddenObjectOpacity = (canvas, opacity)=>{
    canvas.getObjects().forEach((obj)=>{
        if (obj.eflex?.startHidden) {
            obj.opacity = opacity;
        }
    });
    renderAllSafe(canvas);
};
export default class JemDynamicInstruction extends Component {
    @service
    store;
    @service
    intl;
    previousAnimations = this.args.animations;
    previousWorkInstruction = this.args.workInstruction;
    canvas;
    onResize = task({
        drop: true
    }, waitFor(async (element)=>{
        await timeout(getDelayTime(100));
        scaleCanvas(element, this.canvas, this.args.workInstruction);
    }));
    loadCanvas = task({
        enqueue: true
    }, waitFor(async (element)=>{
        await this.store.findRecord('work-instruction', this.args.workInstruction.id, {
            reload: true
        });
        await cleanupCanvas(this.canvas);
        this.canvas = initializeCanvas(element.querySelector('.work-instruction-canvas'), {
            width: this.args.workInstruction.width,
            height: this.args.workInstruction.height,
            hoverCursor: 'default'
        });
        const canvasJson = this.args.workInstruction.deployedCanvas;
        canvasJson.selection = false;
        canvasJson.objects.forEach((obj)=>{
            obj.objectCaching = false;
            obj.selectable = false;
            obj.editable = false;
            if (obj.eflex != null) {
                obj.eflex.originalFillColor = obj.fill;
                obj.eflex.originalStrokeColor = obj.stroke;
                if (obj.type?.toLowerCase() === TextTypes.TEXTBOX) {
                    obj.eflex.originalTextValue = obj.text;
                    obj.eflex.originalTextBackgroundColor = obj.backgroundColor;
                    obj.eflex.originalStrokeWidth = obj.styles[0]?.style?.strokeWidth;
                    obj.eflex.originalFontSize = obj.styles[0]?.style?.fontSize;
                    obj.eflex.originalStyles = clone(obj.styles);
                }
                if (obj.eflex.link?.length > 0) {
                    obj.hoverCursor = 'pointer';
                }
                if (obj.eflex.startHidden && !this.args.previewMode) {
                    obj.opacity = 0;
                }
            }
        });
        await this.canvas.loadFromJSON(canvasJson);
        scaleCanvas(element, this.canvas, this.args.workInstruction);
        prepareLineObjects(this.canvas, this.args.previewMode);
        this.animate.perform(); // not yielded since it can use a while true for flash
    }));
    compareAttrs = task({
        onState: null
    }, waitFor(async (element, [workInstruction, animations, previewMode])=>{
        const workInstructionChanged = this.previousWorkInstruction !== workInstruction;
        const animationsChanged = !dequal(this.previousAnimations, animations);
        this.previousWorkInstruction = workInstruction;
        this.previousAnimations = animations;
        if (!workInstruction || workInstructionChanged || (previewMode && animations != null)) {
            await this.animate.cancelAll({
                resetState: true
            });
        }
        if (workInstructionChanged) {
            await this.loadCanvas.perform(element);
        } else if (animationsChanged || (previewMode && animations != null)) {
            await this.animate.cancelAll({
                resetState: true
            });
            this.animate.perform();
            if (previewMode) {
                await this.resetCanvas.perform();
            }
        }
    }));
    resetCanvas = task({
        restartable: true
    }, waitFor(async ()=>{
        setStartHiddenObjectOpacity(this.canvas, 0);
        await timeout(this.#getDelayTime(this.args.resetTimeout));
        const tasks = [
            this.animate.cancelAll({
                resetState: true
            })
        ];
        tasks.push(this.canvas.getObjects().map((obj)=>this.resetObject.perform(obj)));
        await all(tasks);
        setStartHiddenObjectOpacity(this.canvas, 1);
    }));
    resetObject = task(waitFor(async (obj)=>{
        let fillColor, strokeColor, textBoxColor, color, textValue;
        if (obj.eflex != null) {
            await this.resetObjectOpacity.perform(obj);
            fillColor = obj.eflex.originalFillColor;
            strokeColor = obj.eflex.originalStrokeColor;
        }
        if (obj.type === TextTypes.TEXTBOX) {
            if (obj.eflex != null) {
                obj.styles = clone(obj.eflex.originalStyles);
            }
            if (obj.eflex.originalStyles != null) {
                fillColor = obj.eflex.originalStyles[0]?.style?.textBackgroundColor;
                strokeColor = obj.eflex.originalStyles[0]?.style?.stroke;
                textBoxColor = obj.eflex.originalTextBackgroundColor;
                color = obj.eflex.originalStyles[0]?.style?.fill;
                textValue = obj.eflex.originalTextValue;
            }
            const animation = {
                fillColor,
                strokeColor,
                textBoxColor,
                color,
                textValue
            };
            setTextboxColor(obj, animation);
        } else {
            const animation = {
                fillColor,
                strokeColor,
                textBoxColor,
                color,
                textValue
            };
            setColor(obj, animation, null, null, true);
        }
        renderAllSafe(this.canvas);
    }));
    resetObjectOpacity = task(waitFor(async (obj)=>{
        if (obj.eflex == null || !obj.eflex.isFlashing) {
            return;
        }
        const opacity = obj.eflex.startHidden ? 0 : 1;
        updateObjectProperty(obj.eflex, 'isFlashing', false);
        notifyModified(this.canvas, obj);
        await this._animateOpacity.perform(obj, opacity, {
            duration: 10
        });
    }));
    showObject = task(waitFor(async (obj)=>{
        await this.resetObjectOpacity.perform(obj);
        await this._animateOpacity.perform(obj, 1, {
            duration: ANIMATION_DURATION,
            easing: Fabric.util.ease.easeInQuad
        });
    }));
    hideObject = task(waitFor(async (obj)=>{
        await this.resetObjectOpacity.perform(obj);
        await this._animateOpacity.perform(obj, 0, {
            duration: ANIMATION_DURATION,
            easing: Fabric.util.ease.easeOutQuad
        });
    }));
    flashObject = task(waitFor(async (obj)=>{
        obj.eflex ??= {};
        updateObjectProperty(obj.eflex, 'isFlashing', true);
        notifyModified(this.canvas, obj);
        while(true){
            await all([
                this._animateEaseOutAndIn.perform(obj),
                timeout(this.#getDelayTime(ANIMATION_DURATION * 4))
            ]);
        }
    }));
    _animateEaseOutAndIn = task(waitFor(async (obj)=>{
        await this._animateOpacity.perform(obj, 0, {
            duration: ANIMATION_DURATION,
            easing: Fabric.util.ease.easeOutQuad
        });
        await this._animateOpacity.perform(obj, 1, {
            duration: ANIMATION_DURATION,
            easing: Fabric.util.ease.easeInQuad
        });
    }));
    animate = task({
        enqueue: true
    }, waitFor(async ()=>{
        if (isEmpty(this.args.animations) || !this.canvas) {
            return;
        }
        const animateTasks = [];
        this.args.animations.forEach(async (animation)=>{
            const obj = getCanvasObjectById(this.canvas, animation.object);
            if (obj == null) {
                return null;
            }
            if (isEmpty(this.args.childStatus)) {
                animateTasks.push(this._runAnimation(animation, obj, null, null));
                return;
            }
            const station = this.args.childStatus?.liveBuildStatus?.station;
            const serialNumber = this.args.childStatus?.liveBuildStatus?.serialNumber;
            const currentChildren = this.args.childStatus?.liveBuildStatus?.children;
            const buildData = this.args.childStatus?.buildDatum;
            let processDataValue = null;
            if (currentChildren) {
                processDataValue = this.findProcessDataValue(animation, currentChildren);
            }
            if (isEmpty(animation?.processDataOptionId)) {
                animateTasks.push(this._runAnimation(animation, obj, buildData, null));
                return;
            }
            if (!isBlank(processDataValue)) {
                animateTasks.push(this._runAnimation(animation, obj, buildData, processDataValue));
            } else {
                const result = await this.getProcessDataMatch.perform(serialNumber, station, animation.processDataOptionId);
                if (!isEmpty(result)) {
                    processDataValue = this.findProcessDataValueFromResult(animation, result);
                } else {
                    processDataValue = this.intl.t('noData');
                }
                animateTasks.push(this._runAnimation(animation, obj, buildData, processDataValue));
            }
        });
        await all(animateTasks);
    }));
    findProcessDataValue(animation, children) {
        const matchedValue = children.find((child)=>child?.processData?.some((data)=>data.variableDef === animation.processDataOptionId));
        const matchedItem = matchedValue?.processData?.find((item)=>item.variableDef === animation.processDataOptionId);
        return this.formatProcessDataValue(matchedItem) ?? this.intl.t('noData');
    }
    findProcessDataValueFromResult(animation, result) {
        const matchedValue = result.find((data)=>data?.formattedProcessData?.some((item)=>item.variableDef === animation.processDataOptionId));
        const matchedItem = matchedValue?.formattedProcessData?.find((item)=>item.variableDef === animation.processDataOptionId);
        return this.formatProcessDataValue(matchedItem) ?? this.intl.t('noData');
    }
    formatProcessDataValue(matchedItem) {
        let { value, decimalLength, unitName } = matchedItem ?? {};
        if (value === null && value === '') {
            return null;
        }
        if (!isNaN(value) && value !== true && value !== false) {
            value = Number(value).toFixed(decimalLength ?? 0);
            return unitName ? `${value} ${unitName}` : value;
        }
        if (typeof value === 'string') {
            return unitName ? `${value} ${unitName}` : value;
        }
        if (typeof value === 'boolean') {
            return value ? 'True' : 'False';
        }
        return value;
    }
    _runAnimation(animation, obj, buildData, processData) {
        switch(animation.animation){
            case AnimationTypes.SHOW:
                {
                    setColor(obj, animation, buildData, processData);
                    return this.showObject.perform(obj);
                }
            case AnimationTypes.HIDE:
                {
                    setColor(obj, animation, buildData, processData);
                    return this.hideObject.perform(obj);
                }
            case AnimationTypes.FLASH:
                {
                    setColor(obj, animation, buildData, processData);
                    return this.flashObject.perform(obj);
                }
            case AnimationTypes.RESET:
                {
                    return this.resetObject.perform(obj);
                }
            default:
                {
                    return null;
                }
        }
    }
    _animateOpacity = task(waitFor(async (obj, value, options)=>{
        await new Promise((resolve)=>{
            obj.animate({
                opacity: value
            }, {
                ...options,
                onComplete: resolve,
                onChange: ()=>{
                    renderAllSafe(this.canvas);
                }
            });
        });
    }));
    openLink = task({
        drop: true
    }, waitFor(async (event)=>{
        if (event.target.tagName !== 'CANVAS') {
            return;
        }
        event.preventDefault();
        const getCanvasCoordsForClickFunc = this.args.getCanvasCoordsForClick ?? getCanvasCoordsForClick;
        const { x, y } = getCanvasCoordsForClickFunc(this.canvas, event);
        const obj = getCanvasObjectByPoint(this.canvas, x, y, true);
        const link = obj?.eflex?.link;
        if (isBlank(link)) {
            return;
        }
        if (link.startsWith('red:')) {
            await fetch(link.replace('red:', ''));
        } else {
            window.open(link);
        }
    }));
    async willDestroy() {
        super.willDestroy(...arguments);
        await cleanupCanvas(this.canvas);
    }
    #getDelayTime(time) {
        if (this.args.bypassDelayTime) {
            return time;
        } else {
            return getDelayTime(time);
        }
    }
    getProcessDataMatch = task(waitFor(async (serialNumber, station, processDataOptionId)=>{
        if (generatesSerialNumber(station)) {
            return;
        }
        const searchSerialNumbers = [];
        if (station?.area.bomSource?.constructor.modelName === 'barcode-bom-source') {
            const source = station?.area?.bomSource;
            const start = source?.serialNumberStart - 1;
            const end = start + source?.serialNumberLength;
            const trimmedSerial = serialNumber.slice(start, end);
            if (!isEmpty(trimmedSerial)) {
                searchSerialNumbers.push(trimmedSerial);
            }
        } else {
            searchSerialNumbers.push(serialNumber);
        }
        return [
            ...((await this.store.query('build-status', {
                area: station?.area.id,
                searchTerm: searchSerialNumbers,
                processData: processDataOptionId
            })))
        ];
    }));
    static{
        template_542080cb15f94fae88299a4911e57428(`
    <div
      class="component-jem-dynamic-instruction"
      {{didInsert this.loadCanvas.perform}}
      {{didUpdate this.compareAttrs.perform @workInstruction @animations @previewMode}}
      {{onResize this.onResize.perform}}
      ...attributes
    >
      {{#if this.loadCanvas.isRunning}}
        <Spinner />
      {{/if}}
      <div
        class="work-instruction-display"
        title={{@workInstruction.deployedName}}
        {{on "pointerup" this.openLink.perform}}
      >
        <canvas class="work-instruction-canvas h-100"></canvas>
      </div>
    </div>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
